import React from 'react';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Creature } from '../../types';
import { formatSoldierType } from '../../utils/formatting';
import { createSubtleIceSurface, hashStringToSeed } from '../../theme/surfaces';
import { CreatureCard } from './CreatureCard';

interface CreatureCardMiniProps {
  creature: Creature;
}

export const CreatureCardMini = React.memo(function CreatureCardMini({ creature }: CreatureCardMiniProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const iceSurfaceSx = React.useMemo(
    () => createSubtleIceSurface(hashStringToSeed(creature.id), {}),
    [creature.id]
  );

  return (
    <Card variant="outlined" sx={{ ...iceSurfaceSx, width: 257 }}>
      <CardContent>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {creature.name}
            </Typography>
            <IconButton aria-label={`Expand ${creature.name}`} size="small" onClick={() => setIsExpanded(true)}>
              <OpenInFullIcon fontSize="inherit" />
            </IconButton>
          </Box>
          <Typography component="div" variant="body2" color="text.secondary">
            Type: {formatSoldierType(creature.type, true)}
            {creature.isCustom && <Chip size="small" color="secondary" label="Custom" sx={{ ml: 1 }} />}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            M {creature.move} · F {creature.fight} · S {creature.shoot} · A {creature.armour} · W {creature.will} · H {creature.health}
          </Typography>
        </Stack>
      </CardContent>

      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        fullScreen={fullScreen}
        maxWidth="md"
        className="MemberDialog"
        disableRestoreFocus
      >
        <DialogContent sx={{ m: 1, p: 2, pt: 6 }}>
          <CreatureCard creature={creature} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsExpanded(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});